import { useEffect, useRef, useState } from 'react'
import { Illustration_ } from 'src/api/db/apiProductImages'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { ScrollArea } from 'src/shared/components/ui'
import { Sheet, SheetContent, SheetTitle } from 'src/shared/components/ui/sheet'
import { toast } from 'src/shared/hooks/use-toast'

import { QuantitySelector } from './components/QuantitySelector'
import { ProductHeader } from 'src/shared/components/ProductShared/ProductHeader'
import { ProductPriceDisplay } from 'src/shared/components/ProductShared/ProductPriceDisplay'
import { ProductLikeButton } from 'src/shared/components/ProductShared/ProductLikeButton'
import { FavouriteProductButton } from 'src/shared/components/ProductShared/FavouriteProductButton'
import { KlayderRecommendsSection } from 'src/shared/components/ProductShared/KlayderRecommendsSection'
import { ProductDescription } from 'src/shared/components/ProductShared/ProductDescription'
import { ProductCategories } from 'src/shared/components/ProductShared/ProductCategories'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useSearchParams } from 'react-router-dom'
import { useProductsOpenApi } from 'src/api/db/apiProductsOpen'
import { cn } from 'src/shared/components/ui/utils'
import {
  useLikesApi,
  useUserLikesApi,
  useToggleLikeApi,
} from 'src/api/db/apiLikes'
import { useAuth } from 'src/shared/hooks/authProvider'
import { ProductImageCarousel } from './components/ProductImageCarousel'
import { TabButton } from './components/TabButton'
import { DecorationPicker } from 'src/pages/_myAccount/EditDecoration/components/DecorationPicker'
import { useGetDecorationInfo } from 'src/api/server/useGetDecorationInfo'
import { useProductPreviewMode } from './displayMode'
import { ProposalProductModal } from './ProposalProductModal'

export const ProductPreview = ({
  open,
  setOpen,
  product_id,
  chosen_product_variant_color,
  product_variant_colors,
  product_name,
  product_description,
  moq,
  article_number,
  price,
  klayder_recommends,
  klayder_recommendation_text,
  related_product_ids,
  product_categories,
  addToCurrentQuoteId,
  favouritesOrgId,
  showAddToQuote = true,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  product_id: number
  chosen_product_variant_color: ProductVariantColor_
  product_variant_colors: ProductVariantColor_[]
  product_name: string
  product_description: string
  moq: number
  article_number: string
  price: number
  klayder_recommends: boolean
  klayder_recommendation_text: string
  related_product_ids?: string | string[]
  product_categories?: Array<{ id: number; category?: { title?: string } }>
  addToCurrentQuoteId?: number
  favouritesOrgId?: string | null
  showAddToQuote?: boolean
}) => {
  const mode = useProductPreviewMode()
  if (!open) return null

  // On a proposal the details open as a centred modal with product information
  // only; everywhere else they stay in this side drawer.
  if (mode === 'proposal') {
    return (
      <ProposalProductModal
        open={open}
        setOpen={setOpen}
        product_id={product_id}
        chosen_product_variant_color={chosen_product_variant_color}
        product_variant_colors={product_variant_colors}
        product_name={product_name}
        product_description={product_description}
        klayder_recommends={klayder_recommends}
        klayder_recommendation_text={klayder_recommendation_text}
      />
    )
  }

  const isMobile = window.innerWidth < 640
  const cardRef = useRef<HTMLDivElement>(null)

  const { t } = useLocalization()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedIllustration, setSelectedIllustration] =
    useState<Illustration_ | null>(null)
  const [quantityInput, setQuantityInput] = useState(moq || 1)
  const [selectedSection, setSelectedSection] = useState('overview')

  const previousColorIdRef = useRef<number | null>(null)

  const articleNumbers = Array.isArray(related_product_ids)
    ? related_product_ids
    : typeof related_product_ids === 'string'
    ? related_product_ids.split(',').filter(Boolean)
    : []

  const { productsByIdsOpen: relatedProductsResponse } = useProductsOpenApi({
    article_numbers: articleNumbers,
  })

  const { session } = useAuth()
  const { likeCounts } = useLikesApi({
    productIds: [product_id],
  })
  const { userLikes } = useUserLikesApi({
    productIds: [product_id],
  })
  const { toggleLike } = useToggleLikeApi()

  const { decorations } = useGetDecorationInfo({
    product_id,
    item_quantity: moq || 1,
    item_colors: 1,
  })

  const likeCount = likeCounts.get(product_id) || 0
  const isLiked = userLikes.has(product_id)
  const isAuthenticated = !!session?.user?.id
  const variant = isMobile ? 'mobile' : 'desktop'
  const related_products = relatedProductsResponse?.data
  const categories = product_categories as
    | Array<{ id: number; category?: { title?: string } }>
    | undefined

  const handleToggleLike = () => {
    if (!isAuthenticated) {
      toast({
        title: t(
          'Please sign in to like products',
          'Vänligen logga in för att gilla produkter'
        ),
        variant: 'destructive',
      })
      return
    }
    toggleLike.mutate({
      productId: product_id,
      currentLikeState: isLiked,
    })
  }

  // Get variant color from URL params or fall back to prop
  const variantColorId = searchParams.get('variant_color_id')
  const chosen_product_variant_color_ = variantColorId
    ? product_variant_colors?.find((c) => c.id === Number(variantColorId)) ||
      chosen_product_variant_color
    : chosen_product_variant_color

  const setProduct_variant_color = (color: ProductVariantColor_) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('variant_color_id', color.id.toString())
    setSearchParams(newParams, { replace: true })

    previousColorIdRef.current = color.id
  }

  // Set initial variant color in URL if not present
  useEffect(() => {
    if (open && !variantColorId && chosen_product_variant_color) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set(
        'variant_color_id',
        chosen_product_variant_color.id.toString()
      )
      setSearchParams(newParams, { replace: true })
    }
  }, [
    open,
    variantColorId,
    chosen_product_variant_color,
    searchParams,
    setSearchParams,
  ])

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          className={`${isMobile ? 'w-full' : 'w-[425px]'} p-3 bg-white`}
        >
          <SheetTitle className="sr-only">{product_name}</SheetTitle>
          <ScrollArea className="h-screen w-full" scrollbarHide ref={cardRef}>
            <Column
              id="product-preview"
              className={cn(
                'h-full gap-4 relative pb-8',
                isMobile ? 'w-full min-w-0' : 'w-[400px]'
              )}
            >
              <ProductImageCarousel
                chosen_product_variant_color_={
                  chosen_product_variant_color_ as ProductVariantColor_
                }
                selectedIllustration={selectedIllustration}
                setSelectedIllustration={setSelectedIllustration}
                product_id={product_id}
              />

              <ProductHeader
                showLink={true}
                productName={product_name}
                product_id={product_id}
                chosenColor={chosen_product_variant_color_?.color || ''}
                product_variant_colors={product_variant_colors}
                scrollContainerRef={cardRef}
                setProduct_variant_color={setProduct_variant_color}
              />

              <ProductPriceDisplay price={price} moq={moq} variant={'mobile'} />

              <Row className="gap-2">
                <ProductLikeButton
                  likeCount={likeCount}
                  isLiked={isLiked}
                  isAuthenticated={isAuthenticated}
                  onToggleLike={handleToggleLike}
                  isPending={toggleLike.isPending}
                  variant={variant}
                />
                <FavouriteProductButton
                  productId={product_id}
                  variant={variant}
                  orgIdOverride={favouritesOrgId}
                />
              </Row>

              <div className="relative">
                <div className="w-full gap-6 p-0 overflow-x-auto scrollbar-hide bg-transparent flex">
                  <TabButton
                    selected={selectedSection === 'overview'}
                    onClick={() => setSelectedSection('overview')}
                  >
                    {t('Overview', 'Översikt')}
                  </TabButton>

                  <TabButton
                    selected={selectedSection === 'decorations'}
                    onClick={() => setSelectedSection('decorations')}
                    className="flex items-center"
                  >
                    {t('Decorations', 'Dekorationer')} (
                    {decorations?.length ?? 0})
                  </TabButton>

                  <TabButton
                    selected={selectedSection === 'other'}
                    onClick={() => setSelectedSection('other')}
                    className="flex items-center"
                  >
                    {t('Other', 'Övrigt')}
                  </TabButton>
                </div>
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              </div>

              {selectedSection === 'overview' && (
                <>
                  <KlayderRecommendsSection
                    klayder_recommends={klayder_recommends}
                    klayder_recommendation_text={klayder_recommendation_text}
                    related_products={related_products}
                    variant={'mobile'}
                  />

                  <ProductDescription description={product_description} />

                  {showAddToQuote && (
                    <QuantitySelector
                      quantityInput={quantityInput}
                      setQuantityInput={setQuantityInput}
                      price={price}
                      moq={moq}
                      product_id={product_id}
                      chosen_product_variant_color_id={
                        chosen_product_variant_color_?.id
                      }
                      product_name={product_name}
                      addToCurrentQuoteId={addToCurrentQuoteId}
                    />
                  )}
                </>
              )}

              {selectedSection === 'decorations' && (
                <DecorationPicker
                  readonly
                  decorations={decorations}
                  quoteSubItem={undefined}
                  selectedPrintCode={null}
                  setPrintCode={() => {}}
                  withTitle={false}
                  withCard={false}
                  fullHeight
                />
              )}

              {selectedSection === 'other' && (
                <ProductCategories
                  categories={categories}
                  articleNumber={article_number}
                  variant={variant}
                />
              )}
            </Column>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  )
}
